"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ArrowLeft, Camera, Palette, Upload, Sparkles, CheckCircle } from "lucide-react"
import { StartFromScratchWizard } from "./StartFromScratchWizard"
import UploadRoomWizard from "./UploadRoomWizard"
import { useSession } from "@/app/context/AuthContext"
import { getFromIndexedDB } from "@/src/utils/indexed-db-helper"

type DesignPath = "scratch" | "upload" | null

export function DesignerWizard() {
  const [selectedPath, setSelectedPath] = useState<DesignPath>(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [successMessageData, setSuccessMessageData] = useState<{
    title: string
    description: string
  } | null>(null)
  const { refreshCredits } = useSession()

  useEffect(() => {
    const checkForSavedWizardState = async () => {
      console.log("[v0] DesignerWizard mounted, checking for Stripe return...")
      const urlParams = new URLSearchParams(window.location.search)
      const checkoutParam = urlParams.get("checkout")
      const checkoutSuccess = checkoutParam?.includes("success") ?? false

      console.log("[v0] URL params:", {
        checkoutSuccess,
        checkoutParam,
        allParams: Object.fromEntries(urlParams.entries()),
      })

      if (checkoutSuccess) {
        console.log("[v0] Detected checkout=success, checking for saved wizard state...")

        const uploadRoomSessionState = sessionStorage.getItem("wizard_state_upload-room")
        const scratchSessionState = sessionStorage.getItem("wizard_state_start-from-scratch")

        const uploadRoomIndexedDBState = await getFromIndexedDB("wizard_state_upload-room")
        const scratchIndexedDBState = await getFromIndexedDB("wizard_state_start-from-scratch")

        const hasUploadRoomState = !!(uploadRoomSessionState || uploadRoomIndexedDBState)
        const hasScratchState = !!(scratchSessionState || scratchIndexedDBState)

        console.log("[v0] Storage check:", {
          hasUploadRoomState,
          hasScratchState,
          uploadRoomSessionStateLength: uploadRoomSessionState?.length,
          scratchSessionStateLength: scratchSessionState?.length,
          uploadRoomIndexedDBState: !!uploadRoomIndexedDBState,
          scratchIndexedDBState: !!scratchIndexedDBState,
        })

        if (hasUploadRoomState) {
          console.log("[v0] Found UploadRoom state, auto-launching UploadRoomWizard...")
          setSelectedPath("upload")
        } else if (hasScratchState) {
          console.log("[v0] Found StartFromScratch state, auto-launching StartFromScratchWizard...")
          setSelectedPath("scratch")
        } else {
          console.log("[v0] No saved wizard state found, staying on path selection screen")
        }
      } else {
        console.log("[v0] No checkout=success param, normal page load")
      }
    }

    checkForSavedWizardState()
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("purchase") === "success") {
      const type = urlParams.get("type")
      const plan = urlParams.get("plan")
      const pack = urlParams.get("pack")
      const creditAmount = urlParams.get("credits") || "100"

      let messageData
      if (type === "subscription") {
        const planName = plan === "pro" ? "Pro" : plan === "starter" ? "Starter" : "Premium"
        messageData = {
          title: `${planName} Subscription Successfully Activated!`,
          description: "",
        }
      } else if (type === "credits") {
        messageData = {
          title: `Successfully Added ${creditAmount} Credits!`,
          description: "",
        }
      } else {
        messageData = {
          title: "Purchase Successful!",
          description: "",
        }
      }

      setSuccessMessageData(messageData)
      setShowSuccessMessage(true)
      window.history.replaceState({}, "", "/designer")
      setTimeout(() => {
        setShowSuccessMessage(false)
      }, 10000)

      refreshCredits()
    }
  }, [refreshCredits])

  const successMessage = successMessageData || {
    title: "Purchase Successful!",
    description: "",
  }

  const handlePathSelect = (path: DesignPath) => {
    setSelectedPath(path)
  }

  const handleBackToSelection = () => {
    setSelectedPath(null)
  }

  // Show path selection screen
  if (!selectedPath) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {showSuccessMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 animate-in fade-in duration-500">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-green-800 font-medium">{successMessage.title}</p>
              {successMessage.description && <p className="text-green-700 text-sm">{successMessage.description}</p>}
            </div>
          </div>
        )}

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-4">Choose Your Design Path</h1>
          <p className="text-gray-600 text-lg">Select the approach that best fits your project needs</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Start From Scratch Option */}
          <Card
            className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] border-2 hover:border-[#40A1AB] flex flex-col"
            onClick={() => handlePathSelect("scratch")}
          >
            <CardHeader className="text-center pb-4">
              <div className="mx-auto mb-4 p-4 bg-[#40A1AB]/10 rounded-full w-fit">
                <Sparkles className="h-8 w-8 text-[#40A1AB]" />
              </div>
              <CardTitle className="text-xl">Start From Scratch</CardTitle>
              <CardDescription className="text-base">
                Create a completely new design from your imagination
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow">
              <div className="space-y-3 flex-grow">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#40A1AB] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Perfect for new spaces or complete redesigns</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#40A1AB] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Choose room type, size, style, and colors from scratch</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#40A1AB] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Get fresh design ideas without existing constraints</p>
                </div>
              </div>
              <Button
                className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white mt-4"
                onClick={(e) => {
                  e.stopPropagation()
                  handlePathSelect("scratch")
                }}
              >
                <Palette className="mr-2 h-4 w-4" />
                Start Creating
              </Button>
            </CardContent>
          </Card>

          {/* Upload My Room Option */}
          <Card
            className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] border-2 hover:border-[#40A1AB] flex flex-col"
            onClick={() => handlePathSelect("upload")}
          >
            <CardHeader className="text-center pb-4">
              <div className="mx-auto mb-4 p-4 bg-[#40A1AB]/10 rounded-full w-fit">
                <Camera className="h-8 w-8 text-[#40A1AB]" />
              </div>
              <CardTitle className="text-xl">Upload My Room</CardTitle>
              <CardDescription className="text-base">
                Transform your existing space with AI-powered redesign
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow">
              <div className="space-y-3 flex-grow">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#40A1AB] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Upload a photo of your current room</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#40A1AB] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">AI removes furniture to create a clean canvas</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#40A1AB] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Preserves room layout and fixed elements</p>
                </div>
              </div>
              <Button
                className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white mt-4"
                onClick={(e) => {
                  e.stopPropagation()
                  handlePathSelect("upload")
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload Photo
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Show selected wizard with back button
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {showSuccessMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 animate-in fade-in duration-500">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-green-800 font-medium">{successMessage.title}</p>
            {successMessage.description && <p className="text-green-700 text-sm">{successMessage.description}</p>}
          </div>
        </div>
      )}

      {/* Back to Path Selection */}
      <div className="mb-6">
        <Button variant="outline" onClick={handleBackToSelection} className="mb-4 bg-transparent">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Change Design Path
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#40A1AB]/10 rounded-lg">
            {selectedPath === "scratch" ? (
              <Sparkles className="h-5 w-5 text-[#40A1AB]" />
            ) : (
              <Camera className="h-5 w-5 text-[#40A1AB]" />
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold">
              {selectedPath === "scratch" ? "Start From Scratch" : "Upload My Room"}
            </h2>
            <p className="text-gray-600 text-sm">
              {selectedPath === "scratch" ? (
                <>
                  Creating a new design from your imagination
                  <br />
                  <span className="text-xs italic">
                    Please do not use the back button or reload the browser window during the design steps as progress
                    may be lost.
                  </span>
                </>
              ) : (
                <>
                  Transform your existing space with AI
                  <br />
                  <span className="text-xs italic">
                    Please do not use the back button or reload the browser window during the design steps as progress
                    may be lost.
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Render Selected Wizard */}
      {selectedPath === "scratch" && <StartFromScratchWizard />}
      {selectedPath === "upload" && <UploadRoomWizard />}
    </div>
  )
}
